import {
  ReactNode,
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react'
import { useParams } from 'react-router-dom'
import { useTransactions } from '../hooks/useTransactions'
import { TransactionResponse } from '../services/transactionService/transaction'
import { useDebounce } from '../hooks/useDebounce'

interface FiltersType {
  sellerIds?: string
  customerIds?: string
  teamIds?: string
  startDate?: string
  endDate?: string
  status?: string
}

interface TransactionSellerContextValue {
  setSkip: (value: number) => void
  transaction: {
    isLoading: boolean
    isFetching: boolean
    data: TransactionResponse | undefined
  }
  setSearchValue: React.Dispatch<React.SetStateAction<string | undefined>>
  debouncedSearchValue?: string
  setFilterParams: (value: FiltersType) => void
}
export const TransactionSellerContext = createContext(
  {} as TransactionSellerContextValue,
)

export function TransactionSellerProvider({
  children,
}: {
  children: ReactNode
}) {
  const { id } = useParams()
  const [searchValue, setSearchValue] = useState<string | undefined>(undefined)
  const debouncedSearchValue = useDebounce(500, searchValue)

  const setSkip = (value: number) => {
    setParams((state) => ({ ...state, skip: value }))
  }
  const setFilterParams = (value: FiltersType) => {
    setParams(value)
  }
  const { setParams, ...transaction } = useTransactions({ 
    interval: false,
    useInfiniteScroll: false
  })

  const setSearch = useCallback(() => {
    setParams((state) => ({ ...state, search: debouncedSearchValue }))
  }, [debouncedSearchValue, setParams])

  const setSellerId = useCallback(() => {
    if (id) {
      setParams((state) => ({ ...state, sellerIds: id }))
    }
  }, [id, setParams])

  useEffect(() => {
    setSearch()
  }, [setSearch])

  useEffect(() => {
    setSellerId()
  }, [setSellerId])

  return (
    <TransactionSellerContext.Provider
      value={{
        setSkip,
        transaction,
        setSearchValue,
        debouncedSearchValue,
        setFilterParams,
      }}
    >
      {children}
    </TransactionSellerContext.Provider>
  )
}
